<template>
  <SectionContainer
      :colors="colors"
      :background-variant="backgroundVariant"
      :style="cssVars"
      class="w-full "
      v-if="features.length > 0"
  >
    <div :style="cssVars" class="w-full flex items-center flex-col justify-center px-4 sm:px-1" >
      <div class="max-w-6xl mx-auto text-center">
        <component :is="headingTag" v-if="title" class="text-3xl font-bold" style="color: var(--theme-text-heading);">
          {{ title }}
        </component>
        <p v-if="description" class="text-lg mt-2" style="color: var(--theme-text-body);">
          {{ description }}
        </p>
      </div>

      <!-- Features Grid -->
      <div class="mt-10 flex flex-wrap gap-6 justify-center " >
        <component
            :is="cardComponent"
            v-for="(feature, index) in features"
            :key="index"
            v-motion
            :initial="{ opacity: 0, y: 30 }"
            :visible="{ opacity: 1, y: 0 }"
            :transition="{ duration: 1.5, ease: 'easeOut' }"
            :colors="colors"
            class="p-6 flex flex-row items-center text-center transition-transform w-full md:min-w-96 md:max-w-96 md:w-auto"
            :class="feature.link && feature.link !== '' && 'hover:scale-105 cursor-pointer'"
            @click="feature.link && feature.link !== '' && router.push(back(`${feature.link}`))"
        >
          <div class="flex flex-col items-center justify-center w-full h-full">
            <!-- iconoir:<name> draws that icon (lazy, aria-hidden); a name the map lacks falls back to the check glyph. -->
            <div class="text-4xl mb-4 feat-iconoir" v-if="iconoirName(feature.icon)" aria-hidden="true" :style="{ color: colors.accent }">
              <IconoirIcon :icon="feature.icon"><CheckCircleSolid class="w-6 h-6" /></IconoirIcon>
            </div>
            <div class="text-4xl mb-4" v-else-if="iconMap[feature.icon]">
              <component v-if="feature.icon"
                         :is="iconMap[feature.icon]"
                         class="w-6 h-6 " :style="{ color: colors.accent }"
              />
            </div>
            <div class="text-4xl mb-4" v-else>
              {{ feature.icon }}
            </div>

            <!-- Title -->
            <h3 class="text-xl font-bold" style="color: var(--theme-text-heading);">
              {{ feature.title }}
            </h3>

            <!-- Description -->
            <p class="text-sm mt-2" style="color: var(--theme-text-muted);">
              {{ feature.description }}
            </p>
          </div>
          <div v-if="feature.link && feature.link !== ''" class="flex h-full items-center justify-center"><ChevronDoubleRightIcon class="h-6 w-6 text-[var(--accent-color)]"></ChevronDoubleRightIcon></div>
        </component>
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import { computed } from "vue";
import SectionContainer from "@/components/SectionContainer.vue";
import { StarSolid, CheckCircleSolid, LightBulbOn, DotArrowRight, ArrowRightCircleSolid } from "@iconoir/vue";
import IconoirIcon from "@/components/IconoirIcon.vue";
import {iconoirName} from "@/utils/sectionLogic.js";
import {ChevronDoubleRightIcon} from "@heroicons/vue/24/solid";
import router from "@/router/index.js";
import {useThemedComponent} from "@/composables/useThemedComponent.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const props = defineProps({
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  features: { type: Array, required: true },
  colors: { type: Object, required: true },
  backgroundVariant: { type: Number, default: 1 }
});

const cardComponent = useThemedComponent("card");

// Dynamic styles using brand colors
const cssVars = computed(() => ({
  "--bg-light": "var(--brand-whitest)",
  "--bg-dark": "var(--brand-darkest)",
  "--accent-color": "var(--brand-accent)",
}));

const iconMap = {
  star: StarSolid,
  check: CheckCircleSolid,
  arrow: ArrowRightCircleSolid,
  lightbulb: LightBulbOn,
  dot: DotArrowRight,
};

</script>

<style scoped>
/* Smooth hover effect */
.feat-iconoir { display: flex; justify-content: center; }
.feat-iconoir :deep(.s2-glyph) { width: 1.5rem; height: 1.5rem; }
.shadow-lg:hover {
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.1);
}
</style>
